import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, Hand, Heart, Dumbbell, Trophy, CheckCircle, ArrowRight, Calendar, ChevronDown } from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { BookLink } from "../components/BookLink";
import { SERVICES } from "../config";

const serviceIcons = { 0: Activity, 1: Hand, 2: Heart, 3: Dumbbell, 4: Trophy };

const FAQS = [
    {
        q: "What should I expect at my first visit?",
        a: "Your first visit includes a thorough movement and health assessment, a conversation about your goals, and — when appropriate — your first gentle adjustment. You'll leave with a clear, personalized care plan.",
    },
    {
        q: "Do you offer mobile or in-home visits in DFW?",
        a: "Yes. Chirolane & Wellness serves patients across the Dallas–Fort Worth Metroplex. Reach out by phone or email to discuss visit options for your area.",
    },
    {
        q: "Is chiropractic care safe?",
        a: "Chiropractic care is widely recognized as one of the safest non-invasive approaches to musculoskeletal pain. Dr. Lane tailors every technique to your comfort, health history, and body.",
    },
    {
        q: "How many visits will I need?",
        a: "It depends on your condition and goals. Some patients feel relief in a few visits; corrective and wellness plans build results over time. Your plan is always transparent — no surprises.",
    },
    {
        q: "Do you work with athletes and sports teams?",
        a: "Absolutely. Sports care is a core specialty — from injury recovery and return-to-play planning to performance mobility for athletes at every level.",
    },
];

const Faq = ({ q, a }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="border border-slate-200 rounded-2xl bg-white overflow-hidden">
            <button
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                data-testid={`faq-${q.slice(0, 12).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
            >
                <span className="font-semibold text-[#0A192F] text-sm sm:text-base">{q}</span>
                <ChevronDown className={`h-5 w-5 text-blue-700 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                        <p className="px-6 pb-6 text-sm leading-relaxed text-slate-500">{a}</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default function Services() {
    useEffect(() => {
        document.title = "Services | Chirolane & Wellness, LLC — DFW Chiropractic Care";
    }, []);

    return (
        <main>
            <section className="hero-grain relative bg-gradient-to-b from-blue-50/70 via-slate-50 to-white pt-32 sm:pt-40 pb-16 sm:pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                    <p className="text-xs uppercase tracking-[0.3em] font-semibold text-blue-800 mb-6 flex items-center gap-3">
                        <span className="h-px w-10 bg-blue-700 inline-block" /> Services
                    </p>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.08] text-[#0A192F] max-w-3xl">
                        <MaskedLine delay={0.2}>Comprehensive chiropractic &amp;</MaskedLine>
                        <MaskedLine delay={0.35} className="italic text-blue-700">personalized wellness care</MaskedLine>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.6 }}
                        className="mt-7 text-base sm:text-lg font-light leading-relaxed text-slate-600 max-w-2xl"
                    >
                        Five focused areas of care — each one built around your body, your goals, and the way you move through life in DFW.
                    </motion.p>
                </div>
            </section>

            <Marquee items={["Chiropractic Adjustments", "Manual Therapy", "Wellness Care", "Corrective Exercises", "Sports Care"]} />

            {SERVICES.map((s, i) => {
                const Icon = serviceIcons[i];
                const flip = i % 2 === 1;
                return (
                    <section key={s.id} id={s.id} className={`scroll-mt-28 py-16 sm:py-24 ${i % 2 === 1 ? "bg-slate-50" : "bg-white"}`}>
                        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                            <Reveal className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
                                <div className="relative">
                                    <div className={`absolute -inset-4 rounded-[2.5rem] bg-blue-50 ${flip ? "rotate-2" : "-rotate-2"}`} />
                                    <img src={s.image} alt={`${s.title} at Chirolane & Wellness, DFW`} className="relative rounded-[2rem] aspect-[4/3] w-full object-cover shadow-xl shadow-blue-900/10" loading="lazy" />
                                </div>
                            </Reveal>
                            <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
                                <Reveal>
                                    <div className="flex items-center gap-4">
                                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-700 text-white"><Icon className="h-5 w-5" /></span>
                                        <span className="font-display italic text-5xl text-slate-200 select-none" aria-hidden="true">0{i + 1}</span>
                                    </div>
                                    <h2 className="mt-6 font-display text-3xl sm:text-4xl font-medium tracking-tight text-[#0A192F]">{s.title}</h2>
                                    <p className="mt-2 font-display italic text-xl text-blue-700">{s.tagline}</p>
                                    <p className="mt-5 text-base font-light leading-relaxed text-slate-600">{s.overview}</p>
                                </Reveal>
                                <Reveal delay={0.1}>
                                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
                                        {[["Who it's for", s.whoFor], ["What to expect", s.expect], ["Key benefits", s.benefits]].map(([label, items]) => (
                                            <div key={label}>
                                                <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-800 mb-3">{label}</h3>
                                                <ul className="space-y-2.5">
                                                    {items.map((item) => (
                                                        <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                                                            <CheckCircle className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" />
                                                            {item}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </Reveal>
                                <Reveal delay={0.15}>
                                    <BookLink
                                        data-testid={`service-book-${s.id}`}
                                        className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white text-sm font-semibold px-7 py-3.5 transition-all duration-300 hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-lg"
                                    >
                                        <Calendar className="h-4 w-4" />
                                        Book {s.title}
                                        <ArrowRight className="h-4 w-4" />
                                    </BookLink>
                                </Reveal>
                            </div>
                        </div>
                    </section>
                );
            })}

            <section className="py-20 sm:py-28 bg-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-8">
                    <Reveal className="text-center">
                        <p className="text-xs uppercase tracking-[0.3em] font-semibold text-blue-800 mb-5">Questions</p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0A192F]">
                            Frequently asked <span className="italic text-blue-700">questions</span>
                        </h2>
                    </Reveal>
                    <div data-testid="faq-list" className="mt-12 space-y-4">
                        {FAQS.map((f, i) => (
                            <Reveal key={f.q} delay={i * 0.06}>
                                <Faq q={f.q} a={f.a} />
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2} className="mt-14 text-center">
                        <BookLink
                            data-testid="services-bottom-book-button"
                            className="inline-flex items-center gap-2 rounded-full bg-blue-700 text-white text-sm font-semibold px-9 py-4 transition-all duration-300 hover:bg-[#0A192F] hover:shadow-xl hover:-translate-y-0.5"
                        >
                            <Calendar className="h-4 w-4" />
                            Book an Appointment
                        </BookLink>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
